/**
 * MenuInput — unified menu navigation for the arcade shell
 * (docs/menu-redesign-plan.md §4.2).
 *
 * Keyboard (arrows/WASD/Enter/Space/Escape) and gamepad (D-pad, left stick,
 * A/B/Start) are normalised into {@link MenuAction}s and handed to a single
 * callback — main.ts wires that to `ScreenManager.dispatch`. Mouse and touch
 * stay native (screens attach their own click handlers).
 *
 * Gamepad rules learned the hard way elsewhere: `navigator.getGamepads()` is
 * re-read EVERY frame (Chromium hands out static snapshots — cached pad
 * references never update), presses are rising-edge detected per pad, and the
 * first sighting of a pad seeds state without emitting (no phantom presses).
 *
 * The pure helpers (`actionTypeForKey`, `readPadActions`) carry all the
 * mapping logic and are unit-tested in the node environment.
 */

export type MenuActionType =
  | "up"
  | "down"
  | "left"
  | "right"
  | "confirm"
  | "back"
  | "start"
  | "any";

export interface MenuAction {
  type: MenuActionType;
  source: "keyboard" | "gamepad";
  /** Key auto-repeat (keyboard only — pads emit discrete edges). */
  repeat: boolean;
}

/** Return true to consume (keyboard events get preventDefault). */
export type MenuActionHandler = (action: MenuAction) => boolean | void;

// ── Keyboard mapping (pure) ───────────────────────────────────────────────

/**
 * KeyboardEvent.code → action. Unmapped *content* keys yield "any" (BOOT and
 * TITLE advance on any input); modifiers, F-keys and lock keys yield null so
 * that Alt-Tab, F12 and friends never drive the menu.
 */
export function actionTypeForKey(code: string): MenuActionType | null {
  switch (code) {
    case "ArrowUp":
    case "KeyW":
      return "up";
    case "ArrowDown":
    case "KeyS":
      return "down";
    case "ArrowLeft":
    case "KeyA":
      return "left";
    case "ArrowRight":
    case "KeyD":
      return "right";
    case "Enter":
    case "NumpadEnter":
    case "Space":
      return "confirm";
    case "Escape":
      return "back";
    default:
      break;
  }
  if (/^F\d{1,2}$/.test(code)) return null;
  if (/^(Shift|Control|Alt|Meta|OS)(Left|Right)?$/.test(code)) return null;
  if (
    code === "Tab" ||
    code === "CapsLock" ||
    code === "NumLock" ||
    code === "ScrollLock" ||
    code === "ContextMenu" ||
    code === "PrintScreen" ||
    code === "Pause" ||
    code === ""
  ) {
    return null;
  }
  return "any";
}

// ── Gamepad mapping (pure) ────────────────────────────────────────────────

/** Standard-mapping button index → action. Anything else is "any". */
const PAD_BUTTON_ACTIONS: Readonly<Record<number, MenuActionType>> = {
  0: "confirm", // A / Cross
  1: "back", // B / Circle
  9: "start", // Start / Menu
  12: "up",
  13: "down",
  14: "left",
  15: "right",
};

const AXIS_PRESS = 0.6;
const AXIS_RELEASE = 0.4;

export interface PadReadState {
  buttons: boolean[];
  /** Left-stick latches: -1 (held neg), 0 (neutral), 1 (held pos). */
  latchX: -1 | 0 | 1;
  latchY: -1 | 0 | 1;
}

export interface PadLike {
  buttons: ReadonlyArray<{ pressed: boolean }>;
  axes: ReadonlyArray<number>;
}

/**
 * Diff one pad frame against its previous state.
 * - Buttons: rising edge only. `prev === null` (first sighting) emits nothing.
 * - Left stick: hysteresis latch — fires once past ±0.6, re-arms below ±0.4.
 */
export function readPadActions(
  pad: PadLike,
  prev: PadReadState | null,
): { actions: MenuActionType[]; next: PadReadState } {
  const cur = pad.buttons.map((b) => b.pressed);
  const actions: MenuActionType[] = [];

  if (prev !== null) {
    for (let i = 0; i < cur.length; i++) {
      if (cur[i] && !(prev.buttons[i] ?? false)) {
        actions.push(PAD_BUTTON_ACTIONS[i] ?? "any");
      }
    }
  }

  const ax = pad.axes[0] ?? 0;
  const ay = pad.axes[1] ?? 0;

  let latchX: -1 | 0 | 1;
  let latchY: -1 | 0 | 1;

  if (prev === null) {
    // Seed from current position WITHOUT emitting — a stick already held on
    // first sight must not navigate until it returns to neutral.
    latchX = ax <= -AXIS_PRESS ? -1 : ax >= AXIS_PRESS ? 1 : 0;
    latchY = ay <= -AXIS_PRESS ? -1 : ay >= AXIS_PRESS ? 1 : 0;
  } else {
    latchX = prev.latchX;
    latchY = prev.latchY;

    if (latchY === 0) {
      if (ay <= -AXIS_PRESS) {
        actions.push("up");
        latchY = -1;
      } else if (ay >= AXIS_PRESS) {
        actions.push("down");
        latchY = 1;
      }
    } else if (Math.abs(ay) < AXIS_RELEASE) {
      latchY = 0;
    }

    if (latchX === 0) {
      if (ax <= -AXIS_PRESS) {
        actions.push("left");
        latchX = -1;
      } else if (ax >= AXIS_PRESS) {
        actions.push("right");
        latchX = 1;
      }
    } else if (Math.abs(ax) < AXIS_RELEASE) {
      latchX = 0;
    }
  }

  return { actions, next: { buttons: cur, latchX, latchY } };
}

// ── Runtime (DOM + Gamepad API) ───────────────────────────────────────────

function isEditableTarget(t: EventTarget | null): boolean {
  if (!t || typeof HTMLElement === "undefined" || !(t instanceof HTMLElement)) return false;
  const tag = t.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || t.isContentEditable;
}

export class MenuInput {
  private running = false;
  private rafId = 0;
  private readonly padStates = new Map<number, PadReadState>();

  constructor(private readonly handler: MenuActionHandler) {}

  start(): void {
    if (this.running) return;
    this.running = true;
    window.addEventListener("keydown", this.onKeyDown);
    this.rafId = requestAnimationFrame(this.pollPads);
  }

  stop(): void {
    if (!this.running) return;
    this.running = false;
    window.removeEventListener("keydown", this.onKeyDown);
    cancelAnimationFrame(this.rafId);
    this.rafId = 0;
    this.padStates.clear();
  }

  isRunning(): boolean {
    return this.running;
  }

  private readonly onKeyDown = (ev: KeyboardEvent): void => {
    // Never steal keys from form fields (classic screen inputs, chat, …).
    if (isEditableTarget(ev.target)) return;
    const type = actionTypeForKey(ev.code);
    if (type === null) return;
    const consumed = this.handler({ type, source: "keyboard", repeat: ev.repeat });
    if (consumed === true) ev.preventDefault();
  };

  private readonly pollPads = (): void => {
    if (!this.running) return;
    // Fresh snapshot every frame — never cache Gamepad objects.
    const pads =
      typeof navigator !== "undefined" && navigator.getGamepads ? navigator.getGamepads() : [];
    const seen = new Set<number>();
    for (const pad of pads) {
      if (!pad || !pad.connected) continue;
      seen.add(pad.index);
      const prev = this.padStates.get(pad.index) ?? null;
      const { actions, next } = readPadActions(pad, prev);
      this.padStates.set(pad.index, next);
      for (const type of actions) {
        this.handler({ type, source: "gamepad", repeat: false });
      }
    }
    // Drop state for pads that vanished so a re-connect re-seeds cleanly.
    for (const idx of this.padStates.keys()) {
      if (!seen.has(idx)) this.padStates.delete(idx);
    }
    this.rafId = requestAnimationFrame(this.pollPads);
  };
}
